import type { z, ZodError, ZodType } from "zod";
import chalk from "chalk";
import { stackToBreadcrumbs } from "_shared/_utilities/stacktrace-breadcrumbs";

export function logZodError(
  err: ZodError,
  opts: {
    schemaName: string;
    operation: string;
    payloadSample: unknown;
  }
) {
  const { schemaName: inputName, operation, payloadSample } = opts;
  const stackTrace = new Error("Zod stack trace").stack;
  const { text: stackText, frames: stackFrames } = stackToBreadcrumbs(stackTrace, {
    ignorePatterns: [/zod-error-handler\.ts/i],
  });
  console.group(
    `❌ Schema validation failed for ${inputName ?? "<unknown type>"} during ${
      operation ?? "<unknown operation>"
    } operation`
  );
  console.log("Summary:", err.issues.length, "issue(s)");

  console.group("Issue(s):");
  for (const issue of err.issues) {
    console.log(
      `%c${issue.path.join(".") || "<root>"}%c: (${chalk.gray(
        issue.code === "custom" ? "invalid_type" : issue.code
      )}) ${issue.message}`,
      "font-weight: bold;",
      "font-weight: normal;"
    );
  }
  console.groupEnd();

  console.group("Stack:");
  console.log(stackText);
  console.groupEnd();

  if (payloadSample !== undefined) {
    console.group("Invalid Payload:");
    console.log(payloadSample);
    console.groupEnd();
  }

  console.groupCollapsed("Full Error:");
  console.log(err);
  console.groupEnd();

  console.groupCollapsed("Stack Frames:");
  console.log(stackFrames);
  console.groupEnd();

  console.groupEnd();
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function parseOrThrow<TSchema extends ZodType<any, any, any>>(
  schema: TSchema,
  input: unknown,
  ctx: {
    schemaName: string;
    operation: string;
    payloadSample: unknown;
    logError: boolean;
  }
): z.output<TSchema> {
  const result = schema.safeParse(input);
  if (result.success) return result.data;

  if (ctx.logError) {
    logZodError(result.error, {
      ...ctx,
      schemaName: ctx.schemaName ?? schema.def.type,
      operation: ctx.operation,
      payloadSample: ctx.payloadSample ?? input,
    });
  }

  let zerr = result.error;
  const pretty = zerr.issues.map((i) => `- ${i.path.join(".") || "<root>"}: ${i.message} (${i.code})`).join("\n");

  const err = new Error(
    [`Zod validation failed`, `Schema: ${ctx.schemaName}`, `Operation: ${ctx.operation}`, `Issues:\n${pretty}`]
      .filter(Boolean)
      .join("\n")
  );

  // Preserve the original ZodError as the cause
  (err as ZodError).cause = zerr;
  throw err;
}
